/**
 * Service für Server-Sent Events (SSE) Verbindung zu Live-Ergebnissen
 * Unterstützt mehrere gleichzeitige Verbindungen (eine pro Gruppe)
 */
class ResultsStreamService {
  constructor() {
    this.connections = new Map(); // Map<groupId, {eventSource, onUpdate, reconnectAttempts}>
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000; // 3 Sekunden
  }

  /**
   * Stellt eine SSE-Verbindung für eine Gruppe her
   * @param {number} groupId - ID der Gruppe
   * @param {Function} onUpdate - Callback-Funktion für eingehende Updates
   */
  connect(groupId, onUpdate) {
    // Wenn bereits eine Verbindung für diese Gruppe existiert, trenne sie zuerst
    if (this.connections.has(groupId)) {
      this.disconnectGroup(groupId);
    }

    // Erstelle neue Verbindung
    this.connections.set(groupId, {
      onUpdate,
      reconnectAttempts: 0
    });

    this._establishConnection(groupId, onUpdate);
  }

  /**
   * Stellt die SSE-Verbindung her
   * @private
   */
  _establishConnection(groupId, onUpdate) {
    const connection = this.connections.get(groupId);
    if (!connection) {
      console.warn(`SSE: Verbindung für Gruppe ${groupId} nicht gefunden`);
      return;
    }

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      
      // EventSource unterstützt keine Custom Headers, daher Token als Query-Parameter
      // Alternativ könnte der Endpoint öffentlich sein, da es nur Live-Ergebnisse sind
      let url = `${baseURL}/results/stream/${groupId}`;
      
      // Füge Token hinzu falls vorhanden (für zukünftige Authentifizierung)
      if (typeof window !== 'undefined') {
        const token = localStorage.getItem('authToken');
        if (token) {
          url += `?token=${encodeURIComponent(token)}`;
        }
      }

      console.log(`SSE: Verbinde mit ${url}`);
      const eventSource = new EventSource(url);
      connection.eventSource = eventSource;

      // Event-Handler für eingehende Nachrichten
      eventSource.onmessage = (event) => {
        try {
          if (!event.data) {
            console.warn('SSE: Leere Nachricht empfangen');
            return;
          }
          const data = JSON.parse(event.data);
          console.log('SSE: Nachricht empfangen:', data.type);
          this._handleMessage(data, onUpdate);
        } catch (error) {
          console.error('Fehler beim Parsen der SSE-Nachricht:', error, 'Raw data:', event.data);
        }
      };

      // Event-Handler für Verbindungsfehler
      eventSource.onerror = (error) => {
        const readyState = eventSource?.readyState;
        console.error(`SSE-Verbindungsfehler für Gruppe ${groupId} (readyState: ${readyState}):`, error);
        
        // EventSource.CONNECTING = 0, EventSource.OPEN = 1, EventSource.CLOSED = 2
        if (readyState === EventSource.CLOSED || readyState === EventSource.CONNECTING) {
          // Verbindung wurde geschlossen oder konnte nicht aufgebaut werden - versuche Reconnection
          if (connection.reconnectAttempts < this.maxReconnectAttempts) {
            connection.reconnectAttempts++;
            console.log(`Versuche Reconnection für Gruppe ${groupId} ${connection.reconnectAttempts}/${this.maxReconnectAttempts}...`);
            
            // Schließe alte Verbindung vor Reconnection
            if (eventSource) {
              eventSource.close();
            }
            
            setTimeout(() => {
              // Prüfe ob Verbindung noch existiert
              if (this.connections.has(groupId)) {
                this._establishConnection(groupId, onUpdate);
              }
            }, this.reconnectDelay);
          } else {
            console.error(`Maximale Reconnection-Versuche für Gruppe ${groupId} erreicht. Verbindung wird geschlossen.`);
            this.disconnectGroup(groupId);
            if (onUpdate) {
              onUpdate({
                type: 'error',
                message: 'Verbindung zum Server verloren. Bitte Seite neu laden.'
              });
            }
          }
        }
      };

      // Event-Handler für erfolgreiche Verbindung
      eventSource.onopen = () => {
        console.log(`SSE-Verbindung für Gruppe ${groupId} hergestellt`);
        connection.reconnectAttempts = 0; // Reset bei erfolgreicher Verbindung
      };

    } catch (error) {
      console.error('Fehler beim Herstellen der SSE-Verbindung:', error);
      if (onUpdate) {
        onUpdate({
          type: 'error',
          message: 'Fehler beim Herstellen der Verbindung'
        });
      }
    }
  }

  /**
   * Verarbeitet eingehende SSE-Nachrichten
   * @private
   */
  _handleMessage(data, onUpdate) {
    if (!onUpdate) return;

    switch (data.type) {
      case 'connected':
        console.log(`SSE-Verbindung bestätigt für Gruppe ${data.groupId}`);
        onUpdate({
          type: 'connected',
          groupId: data.groupId
        });
        break;

      case 'result-update':
        // Einzelnes Result-Update
        onUpdate({
          type: 'result-update',
          groupId: data.groupId,
          result: data.result
        });
        break;

      case 'rank-update':
        // Rang-Updates für alle Teilnehmer
        onUpdate({
          type: 'rank-update',
          groupId: data.groupId,
          results: data.results
        });
        break;

      case 'heartbeat':
        // Heartbeat - Verbindung ist aktiv
        // Keine Aktion erforderlich, aber könnte für Debugging verwendet werden
        break;

      default:
        console.warn('Unbekannter SSE-Event-Typ:', data.type);
    }
  }

  /**
   * Trennt die SSE-Verbindung für eine spezifische Gruppe
   * @param {number} groupId - ID der Gruppe
   */
  disconnectGroup(groupId) {
    const connection = this.connections.get(groupId);
    if (connection && connection.eventSource) {
      connection.eventSource.close();
    }
    this.connections.delete(groupId);
  }

  /**
   * Trennt alle SSE-Verbindungen
   */
  disconnect() {
    this.connections.forEach((connection, groupId) => {
      if (connection.eventSource) {
        connection.eventSource.close();
      }
    });
    this.connections.clear();
  }

  /**
   * Prüft ob eine Verbindung für eine Gruppe aktiv ist
   * @param {number} groupId - ID der Gruppe
   * @returns {boolean}
   */
  isConnected(groupId) {
    const connection = this.connections.get(groupId);
    return connection && connection.eventSource && connection.eventSource.readyState === EventSource.OPEN;
  }

  /**
   * Gibt alle aktiven Gruppen-IDs zurück
   * @returns {number[]}
   */
  getConnectedGroupIds() {
    return Array.from(this.connections.keys());
  }
}

// Singleton-Instanz exportieren
export default new ResultsStreamService();

